import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { LoadingState, ErrorState, EmptyState, DemoDataNotice } from "@/components/ui/AsyncState";
import { ConversationStatusBadge } from "@/components/ui/StatusBadge";
import { useApi } from "@/lib/use-api";
import { formatPhone, formatRelativeTime } from "@/lib/format";
import type { ConversationWithPreview } from "@platform/shared";

export function Conversations() {
  const { data, isLoading, error, refetch } = useApi<{ conversations: ConversationWithPreview[] }>(
    "/api/conversations"
  );
  const conversations = data?.conversations ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl text-ink">Conversations</h1>
        <p className="mt-1 text-sm text-ink-soft">Every text thread between Callback and a customer.</p>
      </div>

      {isLoading && <LoadingState label="Loading conversations…" />}
      {error && <ErrorState message={error} onRetry={refetch} />}

      {!isLoading && !error && conversations.length === 0 && (
        <EmptyState>No conversations yet. They'll appear here once a customer texts back.</EmptyState>
      )}

      {conversations.length > 0 && (
        <div className="space-y-3">
          {conversations.map((c) => (
            <Link key={c.id} to={`/dashboard/conversations/${c.id}`}>
              <Card className="flex items-center justify-between px-5 py-4 transition-colors hover:border-brand-300">
                <div className="min-w-0">
                  <p className="font-medium text-ink">{c.leadName ?? formatPhone(c.leadPhone)}</p>
                  <p className="mt-0.5 truncate text-sm text-ink-soft">{c.lastMessage ?? "No messages yet"}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3 pl-4">
                  <span className="text-xs text-ink-soft">
                    {c.lastMessageAt ? formatRelativeTime(c.lastMessageAt) : formatRelativeTime(c.createdAt)}
                  </span>
                  <ConversationStatusBadge status={c.status} />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <DemoDataNotice>
        SMS sending and AI replies aren't live yet — these threads come from the database (seeded/manually entered),
        not a real texting system.
      </DemoDataNotice>
    </div>
  );
}
